import { useEffect, useState, useMemo } from "react";
import { featuresService } from "../../api/features.service";
import type { Feature } from "../../api/features.service";
import { FeatureCard } from "./FeatureCard";
import { SearchBar } from "./SearchBar";
import styles from "./FeaturePicker.module.css";

export type FeaturePickerColumns = {
  mobile?: number;
  tablet?: number;
  desktop?: number;
};

export type FeaturePickerProps = {
  selectedFeatureCodes: string[];
  onChange: (selectedCodes: string[]) => void;
  error?: string;
  disabled?: boolean;
  columns?: FeaturePickerColumns;
  compact?: boolean;
  /**
   * Full-width page layout: the grid auto-fits as many readable columns as the
   * available width allows (ignoring `columns`) and the list grows with the page
   * instead of scrolling inside a short capped region. Leave off inside modals,
   * where the fixed `columns` + inner scroll behaviour is preferred.
   */
  autoFit?: boolean;
};

export function FeaturePicker({
  selectedFeatureCodes,
  onChange,
  error,
  disabled = false,
  columns,
  compact = false,
  autoFit = false,
}: FeaturePickerProps) {
  const [allFeatures, setAllFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    async function fetchFeatures() {
      try {
        setLoading(true);
        setFetchError(null);
        const response = await featuresService.get();
        setAllFeatures(response.features);
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : "Failed to load features");
      } finally {
        setLoading(false);
      }
    }

    fetchFeatures();
  }, []);

  const filteredFeatures = useMemo(() => {
    if (!searchQuery.trim()) {
      return allFeatures;
    }

    const query = searchQuery.toLowerCase();
    return allFeatures.filter(
      feature =>
        feature.name.toLowerCase().includes(query) ||
        feature.code.toLowerCase().includes(query) ||
        feature.description.toLowerCase().includes(query),
    );
  }, [allFeatures, searchQuery]);

  function handleFeatureToggle(featureCode: string) {
    if (disabled) return;

    const isSelected = selectedFeatureCodes.includes(featureCode);
    const newSelection = isSelected
      ? selectedFeatureCodes.filter(code => code !== featureCode)
      : [...selectedFeatureCodes, featureCode];

    onChange(newSelection);
  }

  function handleRetry() {
    setFetchError(null);
    setLoading(true);
    featuresService
      .get()
      .then(response => {
        setAllFeatures(response.features);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setFetchError(err instanceof Error ? err.message : "Failed to load features");
        setLoading(false);
      });
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>Loading features...</div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <p className={styles.errorMessage}>{fetchError}</p>
          <button type="button" onClick={handleRetry} className={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (allFeatures.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>No features available</div>
      </div>
    );
  }

  const selectedCount = selectedFeatureCodes.length;
  const hasNoSelection = selectedCount === 0;

  const gridStyle = {
    "--columns-mobile": columns?.mobile ?? 1,
    "--columns-tablet": columns?.tablet ?? 2,
    "--columns-desktop": columns?.desktop ?? 3,
  } as React.CSSProperties;

  return (
    <div className={styles.container}>
      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        disabled={disabled}
        resultsCount={filteredFeatures.length}
      />

      <div className={styles.selectedInfo}>
        <span className={hasNoSelection ? styles.warningText : undefined}>
          {selectedCount} selected
        </span>
      </div>

      <div className={`${styles.scrollableGrid} ${autoFit ? styles.expanded : ""}`}>
        <div
          className={`${styles.featureGrid} ${autoFit ? styles.autoFit : ""}`}
          style={autoFit ? undefined : gridStyle}
        >
          {filteredFeatures.length === 0 ? (
            <div className={styles.noResults}>No features match "{searchQuery}"</div>
          ) : (
            filteredFeatures.map(feature => (
              <FeatureCard
                key={feature.code}
                feature={feature}
                isSelected={selectedFeatureCodes.includes(feature.code)}
                onToggle={handleFeatureToggle}
                disabled={disabled}
                compact={compact}
              />
            ))
          )}
        </div>
      </div>

      {error && <div className={styles.validationError}>{error}</div>}
    </div>
  );
}
